import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowLeft, Download, Mail, Ban, AlertTriangle, RefreshCw } from 'lucide-react';

import { v3Api } from '../hooks/useApi';
import { formatMinor } from '../Shared/Formatters';
import { StatusPill, PaymentPill, HoldoverPill } from './parts/StatusPill';
import ReconciliationCard from './parts/ReconciliationCard';
import './invoicing.css';

/**
 * Single invoice.
 *
 * Never recomputes totals client-side. Percentage-derived turnover rent and tax
 * are rounded server-side in integer minor units; recomputing them in JS floats
 * would produce a figure that disagrees with the invoice by a fil and with the
 * PDF entirely. Display server values, refetch after every mutation.
 */
export default function InvoiceDetail() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();

    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [voidOpen, setVoidOpen] = useState(false);
    const [voidReason, setVoidReason] = useState('');
    const [holdoverPrompt, setHoldoverPrompt] = useState(null);

    const canManage = localStorage.getItem('manageInvoicing') === '1'
        || localStorage.getItem('usertype') === 'admin';

    const load = useCallback(() => {
        setLoading(true);
        v3Api.invoicing.get(id)
            .then(setInvoice)
            .catch((e) => toast.error(e.response?.data?.message || t('Failed to load invoice')))
            .finally(() => setLoading(false));
    }, [id, t]);

    useEffect(() => { load(); }, [load]);

    const openPdf = (download) => {
        setBusy(true);
        v3Api.invoicing.pdfBlob(id, download)
            .then((blob) => {
                const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
                if (download) {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${invoice?.invoice_number || 'invoice'}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                } else {
                    window.open(url, '_blank', 'noopener');
                }
                // Revoke late — revoking immediately can race the new tab's load.
                setTimeout(() => URL.revokeObjectURL(url), 60000);
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Could not open the PDF')))
            .finally(() => setBusy(false));
    };

    const send = (acknowledgeHoldover = false) => {
        setBusy(true);
        v3Api.invoicing.send(id, acknowledgeHoldover ? { acknowledge_holdover: true } : {})
            .then((res) => {
                if (res.sent) {
                    toast.success(t('Invoice emailed to {{to}}', { to: res.to }));
                } else {
                    // A failed send is recorded server-side and surfaced, never
                    // swallowed — a silent failure on a billing document is worse
                    // than a loud one.
                    toast.warn(t('Not sent: {{reason}}', { reason: res.reason || t('unknown reason') }));
                }
                setHoldoverPrompt(null);
                load();
            })
            .catch((e) => {
                const data = e.response?.data;
                // The backend refuses a holdover invoice with 409 until the
                // caller explicitly acknowledges the expired lease.
                if (data?.requires_acknowledgement === 'holdover') {
                    setHoldoverPrompt(data);
                } else {
                    toast.error(data?.message || t('Send failed'));
                }
            })
            .finally(() => setBusy(false));
    };

    const doVoid = () => {
        if (!voidReason.trim()) return;
        setBusy(true);
        v3Api.invoicing.void(id, voidReason.trim())
            .then(() => {
                toast.success(t('Invoice voided'));
                setVoidOpen(false);
                setVoidReason('');
                load();
            })
            .catch((e) => toast.error(e.response?.data?.message || t('Void failed')))
            .finally(() => setBusy(false));
    };

    if (loading) return <div className="v3 v3-inv"><div className="v3-inv-empty">{t('Loading…')}</div></div>;
    if (!invoice) return <div className="v3 v3-inv"><div className="v3-inv-empty">{t('Invoice not found.')}</div></div>;

    const cur = invoice.currency;
    const isVoid = invoice.status === 'VOID';

    return (
        <div className="v3 v3-inv">
            <div className="v3-inv-header">
                <div>
                    <p className="v3-inv-sub" style={{ marginBottom: 6 }}>
                        <span
                            style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            onClick={() => navigate(`${process.env.PUBLIC_URL}/v3/invoicing`)}
                        >
                            <ArrowLeft size={12} /> {t('Invoices')}
                        </span>
                    </p>
                    <h1 className="v3-inv-title">{invoice.invoice_number || t('Draft invoice')}</h1>
                    <p className="v3-inv-sub" style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                        <StatusPill status={invoice.status} />
                        <PaymentPill paymentStatus={invoice.payment_status} />
                        <HoldoverPill isHoldover={invoice.is_holdover} expiredOn={invoice.lease_expired_on} />
                    </p>
                </div>
                <div className="v3-inv-actions">
                    <button type="button" className="v3-inv-btn" onClick={load} disabled={busy}>
                        <RefreshCw size={14} /> {t('Refresh')}
                    </button>
                    <button type="button" className="v3-inv-btn" onClick={() => openPdf(false)} disabled={busy}>
                        {t('View PDF')}
                    </button>
                    <button type="button" className="v3-inv-btn" onClick={() => openPdf(true)} disabled={busy}>
                        <Download size={14} /> {t('Download')}
                    </button>
                    {canManage && !isVoid && (
                        <button type="button" className="v3-inv-btn" onClick={() => send(false)} disabled={busy}>
                            <Mail size={14} /> {t('Email to tenant')}
                        </button>
                    )}
                    {canManage && !isVoid && (
                        <button type="button" className="v3-inv-btn v3-inv-btn-danger" onClick={() => setVoidOpen(true)} disabled={busy}>
                            <Ban size={14} /> {t('Void')}
                        </button>
                    )}
                </div>
            </div>

            {isVoid && (
                <div className="v3-inv-banner v3-inv-banner-error">
                    <Ban size={16} />
                    <span>{t('This invoice was voided.')} {invoice.void_reason}</span>
                </div>
            )}

            {invoice.is_holdover && !isVoid && (
                <div className="v3-inv-banner v3-inv-banner-warn">
                    <AlertTriangle size={16} />
                    <span>
                        {t('Billed as a holdover: the lease expired on {{date}} but the unit is still trading, so the last known lease terms were used. Confirm the renewal before emailing this to the tenant.', { date: invoice.lease_expired_on })}
                    </span>
                </div>
            )}

            {(invoice.warnings || []).filter((w) => !w.startsWith('Lease expired')).map((w, i) => (
                <div className="v3-inv-banner v3-inv-banner-warn" key={i}>
                    <AlertTriangle size={16} />
                    <span>{w}</span>
                </div>
            ))}

            {/* ── header block ────────────────────────────────────────────── */}
            <div className="v3-inv-card">
                <div className="v3-inv-meta-grid">
                    <Kv label={t('Billed to')} value={invoice.bill_to?.store_name} />
                    <Kv label={t('Tenant code')} value={invoice.bill_to?.tenant_code} />
                    <Kv label={t('Unit')} value={invoice.bill_to?.unit_code} />
                    <Kv label={t('Property')} value={invoice.bill_to?.property_name} />
                    <Kv label={t('Period')} value={`${String(invoice.period_start).slice(0, 10)} → ${String(invoice.period_end).slice(0, 10)}`} />
                    <Kv label={t('Billable days')} value={invoice.billable_days} />
                    <Kv label={t('Invoice date')} value={String(invoice.invoice_date || '').slice(0, 10)} />
                    <Kv label={t('Due date')} value={String(invoice.due_date || '').slice(0, 10)} />
                    <Kv label={t('Revenue share method')} value={invoice.lease_snapshot?.revenue_share_method || t('Not applicable')} />
                    <Kv label={t('Lease terms source')} value={invoice.lease_snapshot?.source === 'leaselog' ? t('Lease change log') : t('Current tenant record')} />
                </div>
            </div>

            {/* ── lines ───────────────────────────────────────────────────── */}
            <div className="v3-inv-card">
                <h2 className="v3-inv-card-title">{t('Line items')}</h2>
                <div className="v3-inv-table-wrap">
                    <table className="v3-inv-table">
                        <thead>
                            <tr>
                                <th>{t('Description')}</th>
                                <th className="v3-inv-num">{t('Net')}</th>
                                <th className="v3-inv-num">{invoice.tax_label || t('VAT')}</th>
                                <th className="v3-inv-num">{t('Total')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(invoice.lines || []).map((l) => (
                                <tr key={l._id}>
                                    <td>
                                        {l.description}
                                        {/* The derivation, so a disputed line is answerable
                                            without re-running the generator. */}
                                        {l.basis?.formula && <span className="v3-inv-basis">{l.basis.formula}</span>}
                                    </td>
                                    <td className="v3-inv-num">{formatMinor(l.net_minor, cur)}</td>
                                    <td className="v3-inv-num">{formatMinor(l.tax_minor, cur)}</td>
                                    <td className="v3-inv-num">{formatMinor(l.total_minor, cur)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="v3-inv-totals">
                    <div className="v3-inv-totals-row">
                        <span>{t('Subtotal')}</span>
                        <span>{formatMinor(invoice.subtotal_minor, cur)}</span>
                    </div>
                    <div className="v3-inv-totals-row">
                        <span>{invoice.tax_label || t('VAT')} @ {invoice.tax_rate || 0}%</span>
                        <span>{formatMinor(invoice.tax_total_minor, cur)}</span>
                    </div>
                    <div className="v3-inv-totals-row grand">
                        <span>{t('Total')}</span>
                        <span>{formatMinor(invoice.total_minor, cur)}</span>
                    </div>
                </div>
            </div>

            {/* Admin-only. Stripped server-side for tenant callers and absent
                from the PDF by design. */}
            <ReconciliationCard invoice={invoice} />

            {/* ── audit trail ─────────────────────────────────────────────── */}
            {(invoice.status_history?.length > 0 || invoice.email_dispatch?.length > 0) && (
                <div className="v3-inv-card">
                    <h2 className="v3-inv-card-title">{t('History')}</h2>
                    <div className="v3-inv-table-wrap">
                        <table className="v3-inv-table">
                            <tbody>
                                {(invoice.status_history || []).map((h, i) => (
                                    <tr key={`s${i}`}>
                                        <td>{String(h.at || '').slice(0, 19).replace('T', ' ')}</td>
                                        <td>{t('Status')} → {h.to}</td>
                                        <td className="v3-inv-muted">{h.by_name || ''} {h.note || ''}</td>
                                    </tr>
                                ))}
                                {(invoice.email_dispatch || []).map((e, i) => (
                                    <tr key={`e${i}`}>
                                        <td>{String(e.at || '').slice(0, 19).replace('T', ' ')}</td>
                                        <td>{e.sent ? t('Emailed') : t('Email failed')}</td>
                                        <td className="v3-inv-muted">{e.to} {e.reason ? `— ${e.reason}` : ''}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── holdover acknowledgement ────────────────────────────────── */}
            {holdoverPrompt && (
                <div className="v3-inv-overlay" onClick={() => setHoldoverPrompt(null)}>
                    <div className="v3-inv-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="v3-inv-modal-title">{t('Confirm before sending')}</h3>
                        <p style={{ fontSize: 13, lineHeight: 1.5 }}>{holdoverPrompt.message}</p>
                        <div className="v3-inv-modal-actions">
                            <button type="button" className="v3-inv-btn" onClick={() => setHoldoverPrompt(null)}>
                                {t('Cancel')}
                            </button>
                            <button type="button" className="v3-inv-btn v3-inv-btn-primary" onClick={() => send(true)} disabled={busy}>
                                {t('Lease terms confirmed — send')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── void ────────────────────────────────────────────────────── */}
            {voidOpen && (
                <div className="v3-inv-overlay" onClick={() => setVoidOpen(false)}>
                    <div className="v3-inv-modal" onClick={(e) => e.stopPropagation()}>
                        <h3 className="v3-inv-modal-title">{t('Void invoice {{number}}', { number: invoice.invoice_number })}</h3>
                        <p style={{ fontSize: 13, lineHeight: 1.5 }}>
                            {t('The invoice keeps its number and stays on record. The period is released so a corrected invoice can be generated, which will receive a new number.')}
                        </p>
                        <div className="v3-inv-field" style={{ marginTop: 12 }}>
                            <span className="v3-inv-label">{t('Reason')}</span>
                            <input
                                className="v3-inv-input"
                                value={voidReason}
                                onChange={(e) => setVoidReason(e.target.value)}
                                placeholder={t('Why is this invoice being voided?')}
                            />
                        </div>
                        <div className="v3-inv-modal-actions">
                            <button type="button" className="v3-inv-btn" onClick={() => setVoidOpen(false)}>{t('Cancel')}</button>
                            <button
                                type="button"
                                className="v3-inv-btn v3-inv-btn-danger"
                                onClick={doVoid}
                                disabled={busy || !voidReason.trim()}
                            >
                                {t('Void invoice')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Kv({ label, value }) {
    return (
        <div>
            <div className="v3-inv-kv-label">{label}</div>
            <div className="v3-inv-kv-value">{value || '—'}</div>
        </div>
    );
}
